import { Popover as RadixPopover } from "radix-ui";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export interface PopoverProps {
	trigger: ReactNode;
	children: ReactNode;
	side?: "top" | "right" | "bottom" | "left";
	align?: "start" | "center" | "end";
	width?: number | string;
	/** Зазор до края экрана при переносе и сдвиге поверхности. */
	collisionPadding?: number;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** Своя вёрстка внутри: снимает паддинг и колоночную раскладку по умолчанию. */
	bare?: boolean;
	className?: string;
	contentLabel?: string;
}

/**
 * Белая r12 поверхность с --shadow-float, отступ 2px от триггера.
 * Появление: 8px смещения + scale 0.96 + снятие блюра за 400ms по --ease-elegant,
 * ровно как .m-popover в моушен-системе — поверхность приезжает оттуда, откуда её позвали.
 */
export function Popover({
	trigger,
	children,
	side = "bottom",
	align = "start",
	width = 240,
	collisionPadding = 8,
	open,
	defaultOpen,
	onOpenChange,
	bare = false,
	className,
	contentLabel,
}: PopoverProps) {
	return (
		<RadixPopover.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
			<RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
			<RadixPopover.Portal>
				<RadixPopover.Content
					side={side}
					align={align}
					sideOffset={2}
					collisionPadding={collisionPadding}
					aria-label={contentLabel}
					// Ширина — пожелание, а не приказ: на узком экране поверхность
					// шире вьюпорта уезжает за край и половина меню оказывается
					// недоступной (жалоба владельца 22.08.2026 — список языков на
					// iPhone). Потолок высоты — та же защита по вертикали, но
					// только для собственной раскладки: в `bare` внутри лежит чужая
					// поверхность со своей тенью, и `overflow` под скролл срезал бы
					// ей эту тень. Там потолок ставит сама вставка, читая ту же
					// переменную (см. `AccountMenu`).
					style={{
						width,
						maxWidth: "var(--radix-popover-content-available-width)",
						...(bare ? {} : { maxHeight: "var(--radix-popover-content-available-height)" }),
					}}
					className={cn(
						// Выше диалога (`z-120`): поповер, открытый из окна, обязан
						// быть над ним, а не под его затемнением.
						"z-130 rounded-card bg-card shadow-float outline-none",
						!bare && "iec-scroll flex flex-col gap-0.5 p-1",
						"data-[state=open]:[animation:iec-reveal_var(--dur-400)_var(--ease-elegant)]",
						"data-[state=closed]:[animation:iec-fade-out_var(--dur-react)_var(--ease-smooth)]",
						className,
					)}
				>
					{children}
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}

export const PopoverClose = RadixPopover.Close;

export interface PopoverItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	icon?: ReactNode;
	/** Разрушающее действие: красный текст и красноватый ховер. */
	danger?: boolean;
	/** Порядковый номер для каскада появления (--stagger = 40ms). */
	index?: number;
	trailing?: ReactNode;
	children?: ReactNode;
}

/**
 * Пункт меню: 40px, r10, иконка + подпись 15px. Ховер заливает строку серым
 * (красноватым у danger) — единственная реакция, никакого сдвига.
 */
export function PopoverItem({
	icon,
	danger = false,
	index = 0,
	trailing,
	className,
	children,
	type = "button",
	...rest
}: PopoverItemProps) {
	return (
		<button
			type={type}
			className={cn(
				"iec-press-glyph iec-menu-item flex h-10 w-full items-center gap-2.5 rounded-control px-2.5 text-left t-body",
				"transition-colors duration-150 ease-smooth",
				"focus-ring",
				"disabled:cursor-not-allowed disabled:opacity-60",
				danger ? "text-danger hover:bg-danger-soft" : "text-fg hover:bg-sunken",
				className,
			)}
			style={{ animationDelay: `calc(${index} * var(--stagger))` }}
			{...rest}
		>
			{icon}
			<span className="min-w-0 flex-grow truncate">{children}</span>
			{trailing}
		</button>
	);
}
